import { useDispatch } from 'react-redux';
import styled, { css, keyframes } from 'styled-components';
import { closeDialog } from '../../store/modules/dialog';
import { useTranslation } from 'react-i18next';
import Dialog from './Dialog';
import Button from '../common/Button';
import { useEffect, useRef, useState } from 'react';
import { DOCUMENT_TYPE, DOC_FORMAT_CONFIG_VALUES } from '../../define/global';
import { downloadFileWithBlob, getFileNameOnly, useExportPDF } from '../../utils/FileControlUtil';
import { useDocInfoSliceSelector } from '../../store/modules/docInfo';
import ga from '../../utils/gaConnect';
import { useGenNotificationPromise } from '../notification/Notification';

import spinnerImage from '../../img/white_spinner.png';
import { MESSAGE_TYPE } from '../../define/extension/message';
import { setMessage } from '../../store/modules/extensionHandle';

const DialogBody = styled.div`
  width: 322px;
  height: 245px;
  background: white;
  display: flex;
  flex-flow: column;
  padding: 0 16px;
  box-sizing: border-box;
`;

const PageRangeText = styled.div`
  width: 100%;
  height: 19px;
  font-size: 13px;
  font-weight: 400;
  color: #2f3133;
  margin: 12px 0 13px 0;
`;

const RadioButtonGroup = styled.div`
  width: 100%;
  height: 78px;
  display: flex;
  flex-direction: column;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 26px;
  position: relative;
  box-sizing: border-box;
`;

const RadioButtonLabel = styled.label`
  position: absolute;
`;

const RadioButtonText = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: #2f3133;
`;

const RadioButton = styled.input`
  cursor: pointer;
  width: 14px;
  height: 14px;
  margin-right: 9px;
  &:disabled {
    pointer-events: none;
    opacity: 0.6;
  }
`;

const RangeInputArea = styled.div`
  width: 100%;
  height: 43px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 6px 0 28px 0;
`;

const RangeInput = styled.input`
  width: 232px;
  height: 24px;
  border-radius: 2px;
  border: solid 1px #b1b6bb;
  margin-bottom: 5px;
`;

const RangeErrorText = styled.div`
  width: 232px;
  height: 14px;
  font-size: 11px;
  font-weight: 400;
  color: #f33;
  ${({ hide }) =>
    hide &&
    css`
      visibility: hidden;
    `};
`;

const ExportButtonArea = styled.div`
  width: 100%;
  height: 30px;
  display: flex;
  justify-content: center;
`;

const play = keyframes`
100% {background-position : -832px;}
`;

const ProgressSpinner = styled.div`
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  background-image: url(${spinnerImage});
  animation: ${play} 0.7s steps(26) infinite;
  //transform: translate(0, -32px);
`;

function ExportPDFDialog({ isShow }) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const docInfoSlice = useDocInfoSliceSelector();
  const notificationPromise = useGenNotificationPromise();
  const onClickCloseBtn = () => dispatch(closeDialog());
  const exportPDF = useExportPDF();
  const [select, setSelect] = useState('');
  const [error, setError] = useState(false);
  const [optionText, setOptionText] = useState(['', '', '']);
  const [optionData, setOptionData] = useState(['', '', '']);
  const [inputText, setInputText] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const inputRef = useRef(undefined);

  const format = DOC_FORMAT_CONFIG_VALUES.sdk.documnetType;

  useEffect(() => {
    switch (format) {
      case DOCUMENT_TYPE.HWP:
      case DOCUMENT_TYPE.WORD:
        setOptionText([
          `${t('ExportPDFDialog.all')}`,
          `${t('ExportPDFDialog.currentPage')}`,
          `${t('ExportPDFDialog.customRange')}`
        ]);
        setOptionData(['all', 'current_page', 'custom_range']);
        setSelect('all');
        break;
      case DOCUMENT_TYPE.SHEET:
        setOptionText([
          `${t('ExportPDFDialog.selectedArea')}`,
          `${t('ExportPDFDialog.entireWorkbook')}`,
          `${t('ExportPDFDialog.selectedSheet')}`
        ]);
        setOptionData(['selected_area', 'entire_workbook', 'selected_sheet']);
        setSelect('selected_area');
        break;
      case DOCUMENT_TYPE.SLIDE:
        setOptionText([
          `${t('ExportPDFDialog.all')}`,
          `${t('ExportPDFDialog.currentSlide')}`,
          `${t('ExportPDFDialog.customRange')}`
        ]);
        setOptionData(['all', 'current_slide', 'custom_range']);
        setSelect('all');
        break;
      default:
        break;
    }
  }, [format]);

  const checkInvalidRangeString = (str) => {
    const testStr = str || '';

    if (testStr === '') return true;
    if (/[0-9]/.test(testStr) === false) return true;

    return /[^0-9,\-]/.test(testStr);
  };

  const onChangeInput = (e) => {
    const str = e.target.value;
    setInputText(str);
    setError(checkInvalidRangeString(str));
  };

  const onSelectChange = (event) => {
    const value = event.target.value;
    setSelect(value);
    setInputText('');

    setError(event.target.value === 'custom_range');
  };

  const onClickExportPDF = () => {
    if (select === 'custom_range' && checkInvalidRangeString(inputText)) {
      setError(true);
      return;
    }

    setIsExporting(true);
    const start = new Date();
    exportPDF({ target: select, customRange: inputText })
      .then((arg) => {
        ga.event({ category: '파일탭', action: 'PDF내보내기' });
        const savedFile = {
          name: `${getFileNameOnly(docInfoSlice.fileName)}.pdf`,
          blob: arg.blob
        };

        downloadFileWithBlob(savedFile.name, savedFile.blob);
        ga.event({
          category: 'PDF 내보내기',
          action: '내보내기',
          label: '파일탭',
          value: new Date() - start
        });
        dispatch(setMessage({ action: MESSAGE_TYPE.ACTION_TYPE.EXPORT_PDF, file: arg }));
        dispatch(closeDialog());
      })
      .catch((err) => {
        dispatch(closeDialog());
        notificationPromise({
          title: i18n.t('Common.Notification'),
          message: i18n.t('ExportPDFDialog.errorMessage'),
          btn: {
            confirm: {
              text: i18n.t('Common.Confirm')
            }
          },
          showCloseBtn: false
        });
      })
      .finally(() => setIsExporting(false));
  };

  return (
    <>
      <Dialog
        title={t('ExportPDFDialog.exportPDF')}
        showCloseBtn
        showTitleSeperateLine
        onClickCloseBtn={onClickCloseBtn}
        isShow={isShow}>
        <DialogBody>
          <PageRangeText>{t('ExportPDFDialog.pageRange')}</PageRangeText>
          <RadioButtonGroup>
            <Item>
              <RadioButton
                type="radio"
                name="radio"
                value={optionData[0]}
                checked={select === optionData[0]}
                disabled={isExporting}
                onChange={(event) => onSelectChange(event)}
              />
              <RadioButtonLabel />
              <RadioButtonText>{optionText[0]}</RadioButtonText>
            </Item>
            <Item>
              <RadioButton
                type="radio"
                name="radio"
                value={optionData[1]}
                checked={select === optionData[1]}
                disabled={isExporting}
                onChange={(event) => onSelectChange(event)}
              />
              <RadioButtonLabel />
              <RadioButtonText>{optionText[1]}</RadioButtonText>
            </Item>
            <Item>
              <RadioButton
                type="radio"
                name="radio"
                value={optionData[2]}
                checked={select === optionData[2]}
                disabled={isExporting}
                onChange={(event) => onSelectChange(event)}
              />
              <RadioButtonLabel />
              <RadioButtonText>{optionText[2]}</RadioButtonText>
            </Item>
          </RadioButtonGroup>
          <RangeInputArea>
            {format !== DOCUMENT_TYPE.SHEET && (
              <>
                <RangeInput
                  type="input"
                  disabled={select !== optionData[2] || isExporting}
                  placeholder={t('ExportPDFDialog.rangeInputPlaceholder')}
                  value={inputText}
                  onChange={onChangeInput}
                  ref={inputRef}
                />
                <RangeErrorText hide={!error}>
                  {t('ExportPDFDialog.customRangeError')}
                </RangeErrorText>
              </>
            )}
          </RangeInputArea>
          <ExportButtonArea>
            <Button
              cssExt={css`
                border-top-left-radius: 2px;
                border-bottom-left-radius: 2px;
                font-weight: 700;
                width: 140px;
                display: flex;
                justify-content: center;
                align-items: center;
              `}
              primary
              disabled={error || isExporting}
              onClick={onClickExportPDF}>
              {isExporting ? (
                <ProgressSpinner width="32px" height="32px" />
              ) : (
                t('ExportPDFDialog.exportPDF')
              )}
            </Button>
          </ExportButtonArea>
        </DialogBody>
      </Dialog>
    </>
  );
}

export default ExportPDFDialog;
